import { Box, Divider, Stack, tokens, Typography } from '@cast/design-system';
import { formatPercentage } from '@cast/utils';

import {
  ScalingPolicyCategory,
  ScalingPoliciesCardItem,
} from '../_hooks/useScalingPoliciesCardData';
import { SCALING_POLICY_CATEGORY_COLORS } from './scalingPoliciesCategoryColors';

const MARKER_SIZE = 8;
const HEADER_INSET = 12;
const ROW_INSET = 24;

const CategoryHeader = ({ category }: { category: ScalingPolicyCategory }) => (
  <Stack direction="row" alignItems="center" gap={4} px={HEADER_INSET} py={5}>
    <Box
      sx={{
        width: MARKER_SIZE,
        height: MARKER_SIZE,
        backgroundColor: SCALING_POLICY_CATEGORY_COLORS[category.key],
        borderRadius: tokens.theme.radius.xs,
      }}
    />
    <Typography variant="L12B" sx={{ flex: 1, textTransform: 'uppercase' }}>
      {category.label}
    </Typography>
    <Typography
      variant="L12B"
      color={tokens.theme.fg.structural.secondary}
      sx={{ textTransform: 'uppercase' }}
    >
      {formatPercentage(category.percentageOfWorkloads, { roundBy: 0 })} of
      workloads
    </Typography>
  </Stack>
);

const PolicyRow = ({ policy }: { policy: ScalingPoliciesCardItem }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={12}
    pl={ROW_INSET}
    pr={HEADER_INSET}
    py={5}
  >
    <Typography variant="P12R" sx={{ flex: 1 }}>
      {policy.label}
    </Typography>
    <Typography variant="P12B">{policy.value}</Typography>
  </Stack>
);

const CategorySection = ({
  category,
  isLast,
}: {
  category: ScalingPolicyCategory;
  isLast: boolean;
}) => {
  const policies = category.topPolicies.filter((policy) => policy.value > 0);

  if (category.workloadCount === 0 && policies.length === 0) {
    return null;
  }

  return (
    <>
      <CategoryHeader category={category} />
      {policies.map((policy) => (
        <PolicyRow key={policy.key} policy={policy} />
      ))}
      {!isLast && <Divider />}
    </>
  );
};

export const ScalingPoliciesTooltip = ({
  categories,
}: {
  categories: ScalingPolicyCategory[];
}) => {
  const visibleCategories = categories.filter(
    (category) =>
      category.workloadCount > 0 ||
      category.topPolicies.some((policy) => policy.value > 0)
  );

  return (
    <Stack sx={{ width: 391, py: 4 }}>
      <Stack px={HEADER_INSET} py={4}>
        <Typography
          variant="P10R"
          color={tokens.theme.fg.structural.secondary}
          sx={{ textTransform: 'uppercase' }}
          style={{ letterSpacing: '1px' }}
        >
          Workload assignment by policy
        </Typography>
      </Stack>
      {visibleCategories.map((category, index) => (
        <CategorySection
          key={category.key}
          category={category}
          isLast={index === visibleCategories.length - 1}
        />
      ))}
    </Stack>
  );
};
